import { DUNNING_NONE, dunningStageLabel, type DunningPolicy } from "@talentkontor/domain";
import type { InvoiceView, Tone } from "./invoice-view";
import { actionLabel } from "./invoice-view";
import { invoiceFilterOf, mailFailedNote, negativerBeleg, type InvoiceFilter } from "./invoice-activity";
import { currentLang, t } from "../lib/sprache";

/**
 * Rechnungsübersicht (K-409): Klartext je Zeile ohne React, damit Liste, Karten am Handy und Tests dieselben Regeln nutzen.
 * Status als Farbpunkt mit Klartext („16 T überfällig“, „Heute fällig“) und Nebenzeile („1. Mahnung fällig in 4 T“), Mahnstufe
 * als Pille nach Stufentyp (Erinnerung gelb, jede Mahnung rot), letzte Aktivität mit Datum, Reiter „Handlungsbedarf“.
 */

export type ListFilter = InvoiceFilter | "handlung";

/** Die Agentur hat einen Knopf zu drücken (kein bloßer Hinweis wie „Frist läuft“ oder „Wartet auf Creator“) */
export function handlungsbedarf(v: Pick<InvoiceView, "next">): boolean {
  return !!v.next && !v.next.inert;
}

export function listFilterMatches(v: InvoiceView, f: ListFilter): boolean {
  if (f === "alle") return true;
  if (f === "handlung") return handlungsbedarf(v);
  return invoiceFilterOf(v) === f;
}

const iso = (s: string | null | undefined) => (s ? String(s).slice(0, 10) : null);
function tage(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}
function plusTage(d: string, n: number): string {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

/** „16 T überfällig“, „in 4 T“: kurze Tagesangabe wie im Handel üblich */
export const tageKurz = (n: number) => t(`${n} T`, `${n} d`);

export interface StatusZeile {
  text: string;
  tone: Tone;
  /** Nebenzeile: was als Nächstes fällig wird bzw. warum nicht gemahnt wird */
  sub: string | null;
}

/**
 * Status in Klartext. Forderungen (an Marke oder Creator) zeigen die Fälligkeit in Tagen; die Nebenzeile sagt, wann die nächste
 * Stufe möglich ist (`nextStage`: Name der nächsten Stufe laut Mahnleiter, falls es eine gibt). Alle anderen Zustände behalten die
 * Bezeichnung der Statuspille.
 */
export function statusZeile(v: InvoiceView, today: string, nextStage: string | null): StatusZeile {
  const s = v.state;
  const due = v.record.effectiveDueDate ?? v.record.documentDueDate ?? null;
  const receivable = v.row.kind !== "talent_to_agency";
  const failed = mailFailedNote(v);
  const withFailed = (sub: string | null) => (failed ? [failed, sub].filter(Boolean).join(" · ") : sub);
  // K-409: negativer Beleg (Storno, Gutschrift) wartet auf den Versand, ist aber keine überfällige Forderung
  if (negativerBeleg(v) && s.state === "send_overdue") return { text: t("Versand ausstehend", "Sending pending"), tone: "warning", sub: withFailed(null) };
  if (receivable && !negativerBeleg(v) && (s.state === "overdue" || s.state === "dunning") && due) {
    const n = Math.max(1, tage(due, today));
    let sub: string | null = null;
    if (v.record.dunningBlocked) sub = t("Mahnsperre", "Dunning block");
    else if (v.next?.labelKey === "hint.dunning_deadline_running" && v.next.params?.["date"]) {
      const bis = String(v.next.params["date"]);
      const inTagen = tage(today, plusTage(bis, 1));
      const was = nextStage ?? t("Inkasso", "Debt collection");
      sub = inTagen <= 0 ? t(`${was} fällig`, `${was} due`) : t(`${was} fällig in ${tageKurz(inTagen)}`, `${was} due in ${tageKurz(inTagen)}`);
    } else if (v.next?.action === "hand_over_to_collection") sub = t("Inkasso prüfen", "Check debt collection");
    else if (s.canDun && nextStage) sub = t(`${nextStage} fällig`, `${nextStage} due`);
    else if (v.next?.labelKey === "hint.talent_turn_until") sub = t("Creator gefragt", "Creator asked");
    return { text: t(`${tageKurz(n)} überfällig`, `${tageKurz(n)} overdue`), tone: "error", sub: withFailed(sub) };
  }
  if (receivable && s.state === "due_today") return { text: t("Heute fällig", "Due today"), tone: "warning", sub: withFailed(null) };
  if (receivable && (s.state === "due_soon" || s.state === "sent") && due && due > today) {
    const n = tage(today, due);
    return { text: t(`Fällig in ${tageKurz(n)}`, `Due in ${tageKurz(n)}`), tone: s.state === "due_soon" ? "warning" : "info", sub: withFailed(null) };
  }
  return { text: v.pill.label, tone: v.pill.tone, sub: withFailed(null) };
}

/** Mahnstufe als Pille: Erinnerung gelb (warning), jede Mahnung rot (error), nach Stufentyp; ohne Stufe null */
export function mahnstufePille(v: Pick<InvoiceView, "record">, policy: DunningPolicy): { label: string; tone: Tone } | null {
  const key = v.record.dunningStage;
  if (!key || key === DUNNING_NONE) return null;
  const stage = policy.stages.find((s) => s.key === key);
  if (!stage) return key === "collection" ? { label: t("Inkasso", "Debt collection"), tone: "error" } : null;
  // Standard-Erinnerung kurz („Erinnerung“), eigene Bezeichnungen und Mahnstufen mit ihrem Namen
  const label = stage.isReminder && key === "reminder" && !stage.label?.trim() ? t("Erinnerung", "Reminder") : dunningStageLabel(policy, key, currentLang());
  return { label, tone: stage.isReminder ? "warning" : "error" };
}

/**
 * Letzte Aktivität mit Datum: der jüngste bekannte Schritt der Rechnung (Mahnung, Zahlung, Zugang, Versand, Freigabe,
 * Einreichung, Antwort des Creators). Bei gleichem Tag gewinnt der spätere Schritt im Ablauf.
 */
export function letzteAktivitaet(v: InvoiceView, policy: DunningPolicy): { label: string; on: string | null } {
  const r = v.record;
  const row = v.row;
  const toCreator = row.kind === "agency_to_talent";
  const incoming = row.kind === "talent_to_agency";
  const kandidaten: [string | null, string][] = [
    [iso(row.created_at), t("Angelegt", "Created")],
    [r.submittedAt, t("Eingereicht", "Submitted")],
    [iso(v.row.approved_at), t("Freigegeben", "Approved")],
    [r.lastSentToBrandAt ?? r.sentToBrandAt, incoming ? t("Im Zahlungslauf", "In payment run") : toCreator ? t("An Creator gesendet", "Sent to creator") : t("Versendet", "Sent")],
    [r.deliveredAt ?? null, t("Zugegangen", "Delivered")],
    [r.deliveryFailedAt ?? null, t("Zustellung gescheitert", "Delivery failed")],
    [r.overdueCheckRequestedAt, t("Creator gefragt", "Creator asked")],
    [r.talentPaymentResponseAt, r.talentPaymentResponse === "paid" ? t("Creator meldet Zahlung", "Creator reports payment") : t("Creator: Geld fehlt", "Creator: money missing")],
    [r.lastDunningAt, r.dunningStage && r.dunningStage !== DUNNING_NONE && policy.stages.some((s) => s.key === r.dunningStage)
      ? dunningStageLabel(policy, r.dunningStage, currentLang()) : t("Gemahnt", "Reminded")],
    [r.adminPaymentConfirmedAt, incoming ? t("Ausgezahlt", "Paid out") : t("Zahlung bestätigt", "Payment confirmed")],
    [iso(row.mail_failed_at ?? null), mailFailedNote(v) ?? t("Mail nicht zugestellt", "Email not delivered")],
    [r.paidAt, incoming ? t("Ausgezahlt", "Paid out") : t("Bezahlt", "Paid")],
    [r.collectionAt, t("An Inkasso übergeben", "Handed to debt collection")],
    [r.cancelledAt, t("Storniert", "Cancelled")],
  ];
  let best: { label: string; on: string | null } = { label: v.pill.label, on: null };
  for (const [on, label] of kandidaten) {
    if (on && (!best.on || on >= best.on)) best = { label, on };
  }
  return best;
}

/**
 * Hauptzeile „Kunde / Betreff“ der Liste (R-01, Umbauten 03.11.2026). Rechnungen der Agentur zeigen den Kunden (Marke bzw.
 * Creator) und darunter Monat · Kooperation. Rechnungen der Creator an die Agentur zeigten bisher die eigene Agentur als
 * Kunden (keine Information); jetzt steht dort die Marke der Kooperation, ohne Marke der Kooperationstitel, sonst der Creator,
 * darunter Monat · Kooperation. Der Kunde im rechtlichen Sinn (Export, Beleg) bleibt die Agentur.
 */
export function kundeZeile(kind: string, x: { agentur: string; creator: string; marke: string; deal: string; monat: string }): { kunde: string; betreff: string } {
  const creator = x.creator && x.creator !== "–" ? x.creator : "";
  if (kind === "talent_to_agency") {
    const kunde = x.marke || x.deal || creator || x.agentur;
    const betreff = [x.monat, kunde !== x.deal ? x.deal : ""].filter(Boolean).join(" · ");
    return { kunde, betreff };
  }
  if (kind === "agency_to_talent") return { kunde: creator || "–", betreff: [x.monat, x.deal].filter(Boolean).join(" · ") };
  return { kunde: x.marke || "–", betreff: [x.monat, x.deal].filter(Boolean).join(" · ") };
}

/** Nächster Schritt: Knopf (nicht träge) oder Hinweis mit Datum („Frist läuft bis 25.10.“) */
/**
 * Knopf in der letzten Spalte der Liste (Inhaber 30.10.2026: „feiner, auf möglichst wenig Zeilen“): ein Wort, die volle
 * Beschriftung steht im Tooltip und in der Schnellansicht
 */
function kurzerSchritt(key: string, kind: string): string | undefined {
  if (kind === "talent_to_agency" && key === "send_to_brand") return t("Vormerken", "Mark");
  const k: Record<string, string> = {
    review: t("Prüfen", "Review"), approve: t("Freigeben", "Approve"), send_to_brand: t("Senden", "Send"), resend_to_brand: t("Erneut senden", "Resend"),
    send_dunning: t("Erinnern", "Remind"), confirm_payment: t("Bestätigen", "Confirm"), confirm_payout_and_ask: t("Bestätigen", "Confirm"),
    request_payment_check: t("Nachfragen", "Ask"), check_payment: t("Prüfen", "Check"), check_refund: t("Prüfen", "Check"),
    hand_over_to_collection: t("Inkasso", "Collection"), extend_due_date: t("Verlängern", "Extend"), payout_rest: t("Auszahlen", "Pay out"), record_transfer: t("Erfassen", "Record"),
  };
  return k[key];
}

export function naechsterSchritt(v: InvoiceView, kurzDatum: (iso: string) => string): { label: string; knopf: boolean; kurz?: string } {
  const a = v.next;
  if (!a) return { label: t("Öffnen", "Open"), knopf: false };
  if (!a.inert) return { label: actionLabel(a, v.row.kind), knopf: true, kurz: kurzerSchritt(a.labelKey.replace(/^action\.|^hint\./, ""), v.row.kind) };
  const d = a.params?.["date"] ? String(a.params["date"]) : null;
  if (a.labelKey === "hint.dunning_deadline_running" && d) return { label: t(`Frist läuft bis ${kurzDatum(d)}`, `Deadline runs until ${kurzDatum(d)}`), knopf: false };
  if (a.labelKey === "hint.talent_turn_until" && d) return { label: t(`Creator am Zug bis ${kurzDatum(d)}`, `Creator's turn until ${kurzDatum(d)}`), knopf: false };
  return { label: actionLabel(a, v.row.kind), knopf: false };
}
